"use client";

import { useEffect } from "react";
import Link from "next/link";
import { LEARNING_PATHS } from "@/lib/learn";
import { toggleCompleted, toggleSaved, useLearning, visitLesson } from "./useLearning";
import { LearnIcon } from "./LearnIcon";
import "./learn.css";

const TITLES: Record<string, string> = {
  "/learn/rules": "The rules of chess",
  "/learn/special-moves/check-and-checkmate": "Check and checkmate",
  "/learn/special-moves/castling": "Castling",
  "/learn/special-moves/stalemate": "Stalemate",
  "/lessons/italian-game": "The Italian Game",
  "/lessons/london-system": "The London System",
  "/lessons/queens-gambit-declined": "The Queen’s Gambit Declined",
  "/lessons/caro-kann-defense": "The Caro-Kann Defense",
  "/endgames/queen-mate": "King and queen against king",
  "/endgames/rook-mate": "King and rook against king",
  "/endgames/square-of-the-pawn": "The square of the pawn",
  "/endgames/king-and-pawn-opposition": "King and pawn opposition",
};

export function LessonProgress({ id }: { id: string }) {
  const progress = useLearning();
  const complete = progress.completed.includes(id);
  const saved = progress.saved.includes(id);
  const path = LEARNING_PATHS.find((item) => item.id === progress.path && (item.lessons as readonly string[]).includes(id)) ?? LEARNING_PATHS.find((item) => (item.lessons as readonly string[]).includes(id)) ?? LEARNING_PATHS[0];
  const index = (path.lessons as readonly string[]).indexOf(id);
  const next = path.lessons.slice(index + 1).find((route) => !progress.completed.includes(route));
  const inPath = (path.lessons as readonly string[]).includes(id);
  const nextTitle = next ? TITLES[next] : undefined;
  useEffect(() => { visitLesson(id); }, [id]);
  return <aside className="learn-lesson-footer" aria-label="Your learning progress">
    <div><p className="learn-kicker">{inPath ? path.title : "One idea at a time"}</p><h2>{complete ? "Another idea, made yours." : "Ready for your next move?"}</h2><p>{inPath && nextTitle ? `Up next: ${nextTitle}. Build on this idea with the next lesson in your path.` : "Return to your journey to choose another idea, or revisit an exercise above."}</p><small>Mark complete when you feel confident. Progress is saved in this browser when storage is available.</small></div>
    <div className="learn-footer-actions">
      <button className="button-primary" onClick={() => toggleCompleted(id)} aria-pressed={complete}><LearnIcon name="check" />{complete ? "Completed · undo" : "Mark as complete"}</button>
      <button className="learn-icon-button" onClick={() => toggleSaved(id)} aria-pressed={saved} aria-label={saved ? "Unsave this lesson" : "Save this lesson"}><LearnIcon name="bookmark" /></button>
      <Link className="learn-text-link" href={inPath && next ? next : "/learn"}>{inPath && next ? `Continue to ${nextTitle ?? "the next lesson"}` : "Choose your next chapter"}<LearnIcon name="arrow" /></Link>
    </div>
    <span className="sr-only" role="status">{complete ? "Lesson marked complete." : "Lesson not marked complete."}</span>
  </aside>;
}
